import { useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchCategory, type CategoryProduct } from '../lib/api';
import { FilterDropdown } from '../components/FilterDropdown';
import { useComparison } from '../hooks/useComparison';

const TIER_OPTIONS = [
  { value: 'all', label: 'All Tiers' },
  { value: 'budget', label: 'Budget' },
  { value: 'mid', label: 'Mid-Range' },
  { value: 'premium', label: 'Premium' },
];

const STYLE_OPTIONS = [
  { value: 'all', label: 'All Styles' },
  { value: 'ultralight', label: 'Ultralight' },
  { value: 'comfort', label: 'Comfort' },
  { value: 'standard', label: 'Standard' },
];

const SORT_OPTIONS = [
  { value: 'all', label: 'Default' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'weight_asc', label: 'Weight: Light to Heavy' },
  { value: 'weight_desc', label: 'Weight: Heavy to Light' },
];

function formatWeight(grams: number): string {
  if (grams >= 1000) {
    return `${(grams / 1000).toFixed(1)}kg`;
  }
  return `${grams}g`;
}

function getTierBadge(tier: CategoryProduct['tier']) {
  switch (tier) {
    case 'budget':
      return (
        <span className="px-2 py-0.5 bg-green-500/10 text-green-400 text-xs rounded">
          Budget
        </span>
      );
    case 'premium':
      return (
        <span className="px-2 py-0.5 bg-purple-500/10 text-purple-400 text-xs rounded">
          Premium
        </span>
      );
    default:
      return null;
  }
}

interface ProductGridCardProps {
  product: CategoryProduct;
  isInComparison?: boolean;
  canAddToComparison?: boolean;
  onToggleComparison?: () => void;
}

function ProductGridCard({
  product,
  isInComparison = false,
  canAddToComparison = true,
  onToggleComparison,
}: ProductGridCardProps) {
  return (
    <div className={`bg-neutral-900 border rounded-lg p-5 transition-colors ${
      isInComparison
        ? 'border-orange-500/50 bg-orange-500/5'
        : 'border-neutral-800 hover:border-neutral-700'
    }`}>
      <div className="flex justify-between items-start gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {getTierBadge(product.tier)}
            {product.style === 'ultralight' && (
              <span className="px-2 py-0.5 bg-cyan-500/10 text-cyan-400 text-xs rounded">
                UL
              </span>
            )}
            {isInComparison && (
              <span className="px-2 py-0.5 bg-orange-500/10 text-orange-400 text-xs rounded">
                Comparing
              </span>
            )}
          </div>
          <h3 className="font-semibold text-white text-lg truncate">
            {product.name}
          </h3>
        </div>
        <div className="text-right shrink-0">
          <p className="font-mono font-bold text-xl text-white">
            £{product.price_gbp}
          </p>
          <p className="font-mono text-sm text-neutral-500">
            {formatWeight(product.weight_g)}
          </p>
        </div>
      </div>
      <p className="text-neutral-400 text-sm leading-relaxed line-clamp-2 mb-3">
        {product.why}
      </p>
      {/* Add to Compare Button */}
      {onToggleComparison && (
        <button
          onClick={onToggleComparison}
          disabled={!isInComparison && !canAddToComparison}
          className={`w-full mt-2 px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
            isInComparison
              ? 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30'
              : canAddToComparison
                ? 'bg-neutral-800 text-neutral-400 hover:text-white hover:bg-neutral-700'
                : 'bg-neutral-800/50 text-neutral-600 cursor-not-allowed'
          }`}
        >
          {isInComparison ? (
            <span className="inline-flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
              </svg>
              In comparison
            </span>
          ) : canAddToComparison ? (
            <span className="inline-flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add to compare
            </span>
          ) : (
            'Max 4 items'
          )}
        </button>
      )}
    </div>
  );
}

function CategoryStructuredData({
  name,
  description,
  productCount,
  slug,
}: {
  name: string;
  description?: string;
  productCount: number;
  slug: string;
}) {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${name} Gear`,
    description: description || `Browse ${productCount} ${name.toLowerCase()} products for your next adventure.`,
    url: `https://notfuckingabout.com/gear/${slug}`,
    numberOfItems: productCount,
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: productCount,
      itemListElement: [],
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
    />
  );
}

export function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // Comparison hook
  const {
    isInComparison,
    toggleProduct,
    canAddMore,
    count: comparisonCount,
    canCompare,
  } = useComparison();

  // Extract filters from URL
  const tier = searchParams.get('tier') || '';
  const style = searchParams.get('style') || '';
  const sort = searchParams.get('sort') || '';

  const { data, isLoading, error } = useQuery({
    queryKey: ['category', slug, tier, style, sort],
    queryFn: () =>
      fetchCategory({
        slug: slug!,
        tier: tier || undefined,
        style: style || undefined,
        sort: sort || undefined,
      }),
    enabled: !!slug,
  });

  // Update document title
  useEffect(() => {
    if (data?.category) {
      const title = data.category.meta_title || `${data.category.name} Gear`;
      document.title = `${title} | NFA`;
    }
    return () => {
      document.title = 'NFA - Gear Recommendations';
    };
  }, [data]);

  const handleFilterChange = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === 'all' || value === '') {
      newParams.delete(key);
    } else {
      newParams.set(key, value);
    }
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setSearchParams({});
  };

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto py-12 px-4 sm:px-6">
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-6xl mx-auto py-12 px-4 sm:px-6">
        <nav className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-neutral-400 hover:text-white text-sm transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Home
          </Link>
        </nav>

        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-8 text-center">
          <h2 className="font-mono text-xl font-bold text-red-400 mb-2">Category Not Found</h2>
          <p className="text-neutral-400">
            The category you're looking for doesn't exist or has been removed.
          </p>
          <Link
            to="/"
            className="inline-block mt-4 px-4 py-2 bg-orange-500 text-black font-medium rounded-lg hover:bg-orange-400 transition-colors"
          >
            Browse all gear
          </Link>
        </div>
      </div>
    );
  }

  const { category, products, meta } = data;
  const hasActiveFilters = tier || style || sort;

  return (
    <div className="max-w-6xl mx-auto py-12 px-4 sm:px-6">
      <CategoryStructuredData
        name={category.name}
        description={category.description}
        productCount={meta.total_products}
        slug={category.slug}
      />

      {/* Breadcrumb */}
      <nav className="mb-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-neutral-400 hover:text-white text-sm transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Home
        </Link>
      </nav>

      {/* Header */}
      <div className="mb-8">
        <h1 className="font-mono text-3xl sm:text-4xl font-bold text-white mb-3">
          {category.name}
        </h1>
        {category.description && (
          <p className="text-lg text-neutral-400 mb-4">
            {category.description}
          </p>
        )}
        <p className="text-sm text-neutral-500">
          {meta.total_products} products
        </p>
      </div>

      {/* Filters */}
      <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-4 mb-8">
        <div className="flex flex-wrap items-end gap-4">
          <FilterDropdown
            label="Tier"
            value={tier || 'all'}
            options={TIER_OPTIONS}
            onChange={(value) => handleFilterChange('tier', value)}
          />
          <FilterDropdown
            label="Style"
            value={style || 'all'}
            options={STYLE_OPTIONS}
            onChange={(value) => handleFilterChange('style', value)}
          />
          <FilterDropdown
            label="Sort"
            value={sort || 'all'}
            options={SORT_OPTIONS}
            onChange={(value) => handleFilterChange('sort', value)}
          />
        </div>
      </div>

      {/* Products Grid */}
      {products.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((product) => (
            <ProductGridCard
              key={product.id}
              product={product}
              isInComparison={isInComparison(product.id)}
              canAddToComparison={canAddMore}
              onToggleComparison={() => toggleProduct(product)}
            />
          ))}
        </div>
      ) : (
        <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-8 text-center">
          <p className="text-neutral-400 mb-4">
            No products found matching your filters.
          </p>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="px-4 py-2 bg-neutral-800 hover:bg-neutral-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* CTA */}
      <div className="mt-12 bg-gradient-to-r from-orange-500/10 to-transparent border border-orange-500/20 rounded-lg p-6 sm:p-8">
        <h2 className="font-mono text-xl sm:text-2xl font-bold text-white mb-3">
          Need a complete kit?
        </h2>
        <p className="text-neutral-400 mb-6">
          Tell us about your trip and we'll put together the perfect gear list.
        </p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 text-black font-bold rounded-lg hover:bg-orange-400 transition-colors"
        >
          <span>Build your kit</span>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </Link>
      </div>

      {/* Floating Comparison Indicator */}
      {comparisonCount > 0 && (
        <div className="fixed bottom-6 right-6 z-50">
          <Link
            to="/compare"
            className={`inline-flex items-center gap-3 px-4 py-3 rounded-full shadow-lg transition-all duration-200 ${
              canCompare
                ? 'bg-orange-500 hover:bg-orange-400 text-black'
                : 'bg-neutral-800 hover:bg-neutral-700 text-white'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <span className="font-medium">
              Compare ({comparisonCount})
            </span>
            {canCompare && (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            )}
          </Link>
        </div>
      )}
    </div>
  );
}
